import React, { useState } from "react";
import { Button, Input, StyledModal } from "@bikdotai/bik-component-library";
import UploadIcon from "../../../icons/UploadIcon";
import { FirebaseService } from "../../../services/firebase/FirebaseService";
import { voidFunction } from "../../../app/messageBlock/Constants";
import { getDownloadURL } from "firebase/storage";
import { useAppSelector } from "../../../state/store";
import { ImageService } from "@bikdotai/bik-component-library";

interface FeatureImageUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImageUpload: (imageUrl: string) => void;
  title?: string;
}

const FeatureImageUploadModal: React.FC<FeatureImageUploadModalProps> = ({
  isOpen,
  onClose,
  onImageUpload,
  title = "Upload image",
}) => {
  const [imageUrl, setImageUrl] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const uploadIntervalRef = React.useRef<NodeJS.Timeout | null>(null);
  const homeState = useAppSelector((state) => state.homeState);
  const firebaseService = new FirebaseService(voidFunction, voidFunction);

  const handleSubmit = () => {
    if (imageUrl.trim()) {
      onImageUpload(imageUrl.trim());
      setImageUrl("");
      onClose();
    }
  };

  const handleFileUpload = () => {
    fileInputRef.current?.click();
  };

  const getFileName = (fileName: string) => {
    const extension = fileName.split(".").pop();
    const date = new Date();
    return `service-features/${date.getTime()}.${extension}`;
  };

  const getMediaPath = (path: string) => {
    return `chatbot/${homeState.storeId || "admin"}/${path}`;
  };

  const uploadImageToFirebase = async (file: File) => {
    // Compress image if it's an image file
    if (file.type.includes("image")) {
      const response = await ImageService.compressImage(file);
      file = response.compressedFile;
    }

    const fileName = getFileName(file.name);
    const uploadRef = firebaseService.getUploadRef(
      file,
      getMediaPath(fileName)
    );

    return new Promise<string>((resolve, reject) => {
      uploadRef.on(
        "state_changed",
        (snapshot) => {
          const percent = Math.round(
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100
          );
          setUploadProgress(percent);
        },
        (error) => {
          console.error("Error uploading image:", error);
          reject(error);
        },
        () => {
          getDownloadURL(uploadRef.snapshot.ref)
            .then((url) => {
              resolve(url);
            })
            .catch(reject);
        }
      );
    });
  };

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      // Check if file is an image
      if (!file.type.startsWith("image/")) {
        alert("Please select an image file (JPEG, PNG, etc.)");
        return;
      }

      // Check file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        alert("File size must be less than 5MB");
        return;
      }

      // Start upload process
      setIsUploading(true);
      setUploadProgress(0);

      try {
        // Upload to Firebase and get the download URL
        const downloadUrl = await uploadImageToFirebase(file);

        // Call the onImageUpload callback with the Firebase URL
        onImageUpload(downloadUrl);
        onClose();

        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      } catch (error) {
        console.error("Error uploading image:", error);
        alert("Error uploading image. Please try again.");
      } finally {
        setIsUploading(false);
        setUploadProgress(0);
      }
    }
  };

  const handleCancel = () => {
    // Clear the upload interval if it exists
    if (uploadIntervalRef.current) {
      clearInterval(uploadIntervalRef.current);
      uploadIntervalRef.current = null;
    }
    setIsUploading(false);
    setUploadProgress(0);

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  if (isUploading) {
    return (
      <StyledModal
        open={isOpen}
        headingTitle=""
        onClose={onClose}
        width="480px"
      >
        <div style={{ padding: "40px 24px", textAlign: "center" }}>
          {/* Icon container with gray background */}
          <div
            style={{
              backgroundColor: "#f5f5f5",
              borderRadius: "50%",
              width: "80px",
              height: "80px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 24px auto",
            }}
          >
            <svg
              width="40"
              height="40"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M4 16L4 17C4 18.1046 4.89543 19 6 19L18 19C19.1046 19 20 18.1046 20 17L20 16M16 12L12 8M12 8L8 12M12 8L12 16"
                stroke="#9e9e9e"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>

          <h3
            style={{
              margin: "0 0 8px 0",
              fontSize: "18px",
              fontWeight: "600",
              color: "#1f2937",
            }}
          >
            Image upload in progress...
          </h3>
          <p
            style={{ margin: "0 0 32px 0", color: "#6b7280", fontSize: "14px" }}
          >
            Your image is getting loaded. This may take a couple of minutes.
          </p>

          {/* Progress bar */}
          <div
            style={{
              width: "100%",
              height: "8px",
              backgroundColor: "#e5e7eb",
              borderRadius: "4px",
              marginBottom: "32px",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                width: `${uploadProgress}%`,
                height: "100%",
                backgroundColor: "#10b981",
                transition: "width 0.15s ease",
              }}
            />
          </div>

          <button
            onClick={handleCancel}
            style={{
              backgroundColor: "transparent",
              border: "1px solid #d1d5db",
              color: "#6b7280",
              borderRadius: "8px",
              padding: "12px 24px",
              fontSize: "14px",
              fontWeight: "500",
              cursor: "pointer",
              transition: "all 0.2s ease",
            }}
          >
            Cancel
          </button>
        </div>
      </StyledModal>
    );
  }

  return (
    <StyledModal
      open={isOpen}
      headingTitle={title}
      onClose={onClose}
      width="480px"
    >
      <div style={{ padding: "24px" }}>
        {/* Dashed border container */}
        <div
          style={{
            border: "2px dashed #d1d5db",
            borderRadius: "12px",
            padding: "32px 24px",
            textAlign: "center",
            marginBottom: "24px",
          }}
        >
          {/* Icon container with gray background */}
          <div
            style={{
              backgroundColor: "#f5f5f5",
              borderRadius: "50%",
              width: "80px",
              height: "80px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 20px auto",
            }}
          >
            <svg
              width="40"
              height="40"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M3 16.5V18.75C3 19.9926 4.00736 21 5.25 21H18.75C19.9926 21 21 19.9926 21 18.75V16.5M16.5 12L12 7.5M12 7.5L7.5 12M12 7.5V16.5"
                stroke="#9e9e9e"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>

          <h3
            style={{
              margin: "0 0 8px 0",
              fontSize: "18px",
              fontWeight: "600",
              color: "#1f2937",
            }}
          >
            Add image
          </h3>
          <p
            style={{ margin: "0 0 24px 0", color: "#6b7280", fontSize: "14px" }}
          >
            JPEG/ PNG format • Max 5MB
          </p>

          <div style={{ marginBottom: "16px", textAlign: "left" }}>
            <p
              style={{
                margin: "0 0 8px 0",
                fontSize: "14px",
                color: "#374151",
                fontWeight: "500",
              }}
            >
              Media link
            </p>
            <Input
              value={imageUrl}
              onChangeText={setImageUrl}
              placeholder="Enter URL of the image"
            />
            {imageUrl.trim() && (
              <div style={{ marginTop: "8px" }}>
                <Button
                  buttonText="Add Image"
                  onClick={handleSubmit}
                  size="medium"
                  style={{
                    backgroundColor: "#731DCF",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    padding: "8px 16px",
                    fontSize: "14px",
                    cursor: "pointer",
                  }}
                />
              </div>
            )}
          </div>

          <p style={{ margin: "16px 0", color: "#6b7280", fontSize: "14px" }}>
            Or upload media from device
          </p>

          {/* Upload button matching the main modal style */}
          <div
            onClick={handleFileUpload}
            style={{
              border: "1px solid #731DCF",
              borderRadius: "8px",
              padding: "12px 16px",
              cursor: "pointer",
              backgroundColor: "transparent",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              transition: "border-color 0.2s ease",
              width: "100%",
            }}
          >
            <UploadIcon width="20" height="20" fill="#731DCF" />
            <span
              style={{ fontSize: "14px", fontWeight: "500", color: "#731DCF" }}
            >
              {isUploading ? "Uploading..." : "Upload"}
            </span>
          </div>

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            style={{ display: "none" }}
          />
        </div>
      </div>
    </StyledModal>
  );
};

export default FeatureImageUploadModal;


